import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { Subscription } from 'rxjs';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { MyHrShellSummary } from '../shared/my-hr-workspace.models';
import { MyHrWorkspaceService } from '../shared/my-hr-workspace.service';
import { MyHrNavComponent } from '../shared/my-hr-nav.component';

@Component({
  selector: 'app-my-hr-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    ButtonModule,
    PageHeroComponent,
    PageShellComponent,
    MyHrNavComponent
  ],
  templateUrl: './my-hr-shell.component.html',
  styleUrls: ['./my-hr-shell.component.scss']
})
export class MyHrShellComponent implements OnInit, OnDestroy {
  private readonly permissionService = inject(PermissionService);
  private readonly workspaceService = inject(MyHrWorkspaceService);
  private readonly subscriptions = new Subscription();

  readonly canViewSelf = this.permissionService.hasPermission(app_permissions.hr.self.view);
  readonly canViewManager = this.permissionService.hasPermission(app_permissions.hr.manager.view);
  readonly canViewExpenses = this.permissionService.hasPermission(app_permissions.expense.records.view);
  readonly canManageExpenses = this.permissionService.hasPermission(app_permissions.expense.records.create);
  readonly canApproveExpenses = this.permissionService.hasPermission(app_permissions.expense.approval.view);

  loading = false;
  summary?: MyHrShellSummary;

  ngOnInit(): void {
    this.loadSummary();
    this.subscriptions.add(this.workspaceService.refreshRequested$.subscribe(() => this.loadSummary()));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  refresh(): void {
    this.loadSummary();
  }

  private loadSummary(): void {
    this.loading = true;
    this.workspaceService.loadShellSummary(
      this.canViewSelf,
      this.canViewManager,
      this.canViewExpenses || this.canManageExpenses,
      this.canApproveExpenses
    ).subscribe({
      next: summary => {
        this.summary = summary;
      },
      error: () => {
        this.summary = undefined;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }
}
